import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Button,
  Collapse,
  Alert,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';
import axios from 'axios';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CodeIcon from '@mui/icons-material/Code';
import { formatDistanceToNow } from 'date-fns';
import PromptsDialog from './PromptsDialog';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface HistoryItem {
  id: string;
  rfpId: string;
  rfpName: string;
  status: 'in_progress' | 'completed' | 'failed';
  errorMessage?: string;
  progressPercentage: number;
  startedAt: string;
  completedAt?: string;
  createdAt: string;
  sectionsCount: number;
  totalCost?: number;
  duration?: number;
}

interface HistoryCardProps {
  item: HistoryItem;
  onView: (proposalId: string) => void;
  onRetry: (rfpId: string) => void;
  onDelete: (proposalId: string) => void;
}

export default function HistoryCard({ item, onView, onRetry, onDelete }: HistoryCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [promptsDialogOpen, setPromptsDialogOpen] = useState(false);
  const [prompts, setPrompts] = useState<{
    questionExtraction?: string;
    answerGeneration?: string;
    cohesionReview?: string;
  }>({});
  const [loadingPrompts, setLoadingPrompts] = useState(false);

  const getStatusIcon = () => {
    switch (item.status) {
      case 'completed':
        return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      case 'failed':
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      case 'in_progress':
        return (
          <AutorenewIcon
            sx={{
              color: 'primary.main',
              animation: 'spin 2s linear infinite',
            }}
          />
        );
    }
  };

  const getStatusColor = () => {
    switch (item.status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'in_progress':
        return 'primary';
    }
  };

  const getStatusLabel = () => {
    switch (item.status) {
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'in_progress':
        return 'In Progress';
    }
  };

  const getTimestamp = () => {
    if (item.status === 'completed' && item.completedAt) {
      return `Completed ${formatDistanceToNow(new Date(item.completedAt), { addSuffix: true })}`;
    }
    if (item.status === 'failed' && item.completedAt) {
      return `Failed ${formatDistanceToNow(new Date(item.completedAt), { addSuffix: true })}`;
    }
    return `Started ${formatDistanceToNow(new Date(item.startedAt), { addSuffix: true })}`;
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return null;
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes === 0) {
      return `${remainingSeconds}s`;
    }
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatCost = (cost?: number) => {
    if (cost === undefined || cost === null) return null;
    return `$${cost.toFixed(4)}`;
  };

  const handleViewPrompts = async () => {
    setLoadingPrompts(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/rfp/history/${item.id}/prompts`, {
        withCredentials: true,
      });
      setPrompts(response.data.prompts || {});
      setPromptsDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch prompts:', error);
      // Still open dialog to show "not available" message
      setPrompts({});
      setPromptsDialogOpen(true);
    } finally {
      setLoadingPrompts(false);
    }
  };

  return (
    <Card
      elevation={2}
      sx={{
        transition: 'all 0.2s ease',
        '&:hover': {
          elevation: 4,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent>
        <Stack spacing={2}>
          {/* Header */}
          <Box display="flex" alignItems="flex-start" justifyContent="space-between">
            <Box flex={1}>
              <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                {getStatusIcon()}
                <Typography variant="h6" component="h3" fontWeight={600}>
                  {item.rfpName}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {getTimestamp()}
              </Typography>
              {item.status === 'completed' && (
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {item.sectionsCount} section{item.sectionsCount !== 1 ? 's' : ''}
                  </Typography>
                  {formatCost(item.totalCost) && (
                    <>
                      <Typography variant="caption" color="text.secondary">•</Typography>
                      <Typography variant="caption" color="success.main" fontWeight={600}>
                        {formatCost(item.totalCost)}
                      </Typography>
                    </>
                  )}
                  {formatDuration(item.duration) && (
                    <>
                      <Typography variant="caption" color="text.secondary">•</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDuration(item.duration)}
                      </Typography>
                    </>
                  )}
                </Stack>
              )}
            </Box>
            <Chip
              icon={getStatusIcon()}
              label={getStatusLabel()}
              color={getStatusColor()}
              size="small"
            />
          </Box>

          {/* Progress Bar for In Progress */}
          {item.status === 'in_progress' && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Generating proposal...
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  {item.progressPercentage}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={item.progressPercentage}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'action.hover',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          )}

          {/* Error Message */}
          {item.status === 'failed' && item.errorMessage && (
            <>
              <Button
                size="small"
                onClick={() => setExpanded(!expanded)}
                endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ alignSelf: 'flex-start' }}
              >
                {expanded ? 'Hide' : 'Show'} error details
              </Button>
              <Collapse in={expanded}>
                <Alert severity="error" icon={<ErrorIcon />}>
                  <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                    {item.errorMessage}
                  </Typography>
                </Alert>
              </Collapse>
            </>
          )}

          {/* Actions */}
          <Box display="flex" justifyContent="flex-end" gap={1}>
            {item.status === 'completed' && (
              <>
                <Tooltip title="View proposal">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() => onView(item.id)}
                  >
                    View
                  </Button>
                </Tooltip>
                <Tooltip title="View prompts used">
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<CodeIcon />}
                    onClick={handleViewPrompts}
                    disabled={loadingPrompts}
                  >
                    Prompts
                  </Button>
                </Tooltip>
              </>
            )}
            {item.status === 'failed' && (
              <Tooltip title="Retry generation">
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={() => onRetry(item.rfpId)}
                >
                  Retry
                </Button>
              </Tooltip>
            )}
            {item.status !== 'in_progress' && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onDelete(item.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Stack>
      </CardContent>

      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>

      <PromptsDialog
        open={promptsDialogOpen}
        rfpName={item.rfpName}
        prompts={prompts}
        onClose={() => setPromptsDialogOpen(false)}
      />
    </Card>
  );
}
